import React, { useState } from "react";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";

const fmt$ = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** v411.0 — owner god-mode correction for a finalized settlement whose
 *  per-item basis was entered wrong (the Paul Chester $0-credit incident).
 *  Edit each line's unit basis (= what the HOUSE keeps); the leftover of the
 *  transfer becomes the distributor's credit and the payout reopens. */
export function FixCreditDialog({ settlement, onClose, onFixed }) {
  const gross = Number(settlement.gross_amount || 0);
  const name = settlement.assigned_distributor_name || "the distributor";
  const [lines, setLines] = useState(
    (settlement.items || []).map((it) => ({
      movement_log_id: it.movement_log_id,
      product_name: it.product_name,
      quantity: Number(it.quantity || 0),
      unit_basis: String(it.unit_basis ?? ""),
    })));
  const [busy, setBusy] = useState(false);

  const allocated = +lines
    .reduce((s, l) => s + (Number(l.unit_basis) || 0) * l.quantity, 0)
    .toFixed(2);
  const credit = +(gross - allocated).toFixed(2);

  const save = async () => {
    if (lines.some((l) => l.unit_basis === "" || Number(l.unit_basis) < 0)) {
      toast.error("Every line needs a unit price (0 or more)");
      return;
    }
    if (!window.confirm(
      `Correct this settlement?\n\nHouse keeps ${fmt$(allocated)} · ${name} will be owed ${fmt$(credit)}.\n${credit !== 0 ? "The payout reopens as pending." : "Credit stays $0."}`)) return;
    setBusy(true);
    try {
      await api.post(`/settlements/${settlement.id}/fix-credit`, {
        items: lines.map((l) => ({ movement_log_id: l.movement_log_id, unit_basis: Number(l.unit_basis) })),
        note: `credit corrected (was ${fmt$(settlement.distributor_credit ?? 0)})`,
      });
      toast.success(`Corrected — ${name} is now owed ${fmt$(credit)}`);
      onFixed();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-zinc-900 rounded-xl w-full max-w-lg p-5 space-y-3"
        onClick={(e) => e.stopPropagation()} data-testid="fix-credit-dialog">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Fix credit — {settlement.received_from} ({fmt$(gross)})</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="fix-credit-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <p className="text-xs text-zinc-500">
          Each price below is what the <b>house keeps per unit</b> ({name}'s wholesale cost).
          Whatever is left of the transfer becomes {name}'s credit. Stock is not touched.
        </p>
        {lines.map((l, idx) => (
          <div key={l.movement_log_id} className="flex items-center gap-2 text-sm" data-testid={`fix-line-${idx}`}>
            <span className="flex-1 truncate">{l.quantity}× {l.product_name}</span>
            <BrutalInput type="number" step="0.01" min="0" className="w-28" value={l.unit_basis}
              onChange={(e) => setLines((prev) => prev.map((x, i) => i === idx ? { ...x, unit_basis: e.target.value } : x))}
              data-testid={`fix-line-basis-${idx}`} />
          </div>
        ))}
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm space-y-1">
          <div className="flex justify-between text-xs text-zinc-600">
            <span>House keeps</span><span className="tabular-nums font-medium">{fmt$(allocated)}</span>
          </div>
          <div className="flex justify-between border-t border-zinc-200 pt-1">
            <span className="text-xs font-semibold text-zinc-700">{name} is owed</span>
            <span className={`text-base font-semibold tabular-nums ${credit > 0 ? "text-emerald-700" : credit < 0 ? "text-rose-700" : "text-zinc-500"}`}
              data-testid="fix-credit-preview">
              {fmt$(credit)}
            </span>
          </div>
        </div>
        <BrutalButton className="w-full" onClick={save} disabled={busy} data-testid="fix-credit-save">
          {busy ? "Saving…" : "Correct credit"}
        </BrutalButton>
      </div>
    </div>
  );
}
